import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';

import { AlertDisplayMode, AlertState, AlertType, NotificationFacade, PurchaseService } from '@ridango/components';

import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';

/**
 * Inline Alert Component (V1)
 * Displays alerts inline on the page for desktop/web applications.
 */
@Component({
    selector: 'rwidget-ui-inline-alert',
    templateUrl: './ui-inline-alert.component.html'
})
export class UiInlineAlertComponent implements OnInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();

    @Input() duration: number;
    /** Optional target id. If provided, this instance will only show alerts for that target. */
    @Input() target: string;
    @Input() assetsPath = 'assets';

    notifications: AlertState[] = [];
    selectedPreference$: Observable<string>;
    AlertType = AlertType;

    constructor(
        private notification: NotificationFacade,
        private purchaseService: PurchaseService,
        @Inject('ENV') public env: Env
    ) {}

    ngOnInit() {
        const alerts$ = this.target
            ? this.notification.getAlertsByTargetAndMode(this.target, AlertDisplayMode.Inline)
            : this.notification.onAlert();

        alerts$.pipe(takeUntil(this.componentDestroyed$)).subscribe((alerts) => {
            this.notifications = alerts.filter(
                (alert, index, self) =>
                    index === self.findIndex((a) => a.message === alert.message && a.type === alert.type)
            );

            // auto close alert if required
            this.notifications.forEach((alert) => {
                const duration = alert.duration || this.duration;
                if (duration) {
                    setTimeout(() => this.notification.remove(alert), duration);
                }
            });
        });

        this.selectedPreference$ = this.purchaseService.currentPreference.pipe(
            map((preference) => {
                return preference?.description ?? preference?.value;
            })
        );
    }

    clear(alert: AlertState) {
        this.notification.remove(alert);
    }

    isSuccess(alert: AlertState) {
        return alert.type?.toLowerCase() === AlertType.Success.toLowerCase();
    }

    isError(alert: AlertState) {
        return alert.type?.toLowerCase() === AlertType.Error.toLowerCase();
    }

    isInfo(alert: AlertState) {
        return alert.type?.toLowerCase() === AlertType.Info.toLowerCase();
    }

    isWarning(alert: AlertState) {
        return alert.type?.toLowerCase() === AlertType.Warning.toLowerCase();
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
}
